import type { AnalysisSetup, ParseSummary } from '../types';
import { codePointLength, truncateCodePoints } from './format-ui';

/**
 * Pure state for the "이름 정리" screen: rename, pick 나, merge rows that are
 * the same person (with undo) and name a group room. Every senderId stays in
 * exactly one row, so toAnalysisSetup() always satisfies the AnalysisSetup
 * contract once validateCleanup() allows submitting.
 */

export const MAX_NAME_LENGTH = 20;
export const MAX_TITLE_LENGTH = 30;
export const MIN_PEOPLE = 2;

export interface CleanupRow {
  /** Stable key: "p" + the smallest senderId among the members. */
  readonly key: string;
  /** Current input value (may be blank or untrimmed while typing). */
  readonly name: string;
  /** Used when the input is left blank. */
  readonly defaultName: string;
  /** Sorted senderIds. */
  readonly members: readonly number[];
  /** Original labels, busiest first. */
  readonly rawNames: readonly string[];
  readonly messageCount: number;
  readonly firstAt: number;
  readonly lastAt: number;
  readonly exporterHint: boolean;
}

interface CleanupSnapshot {
  readonly rows: readonly CleanupRow[];
  readonly meKey: string | null;
}

export interface CleanupState {
  readonly rows: readonly CleanupRow[];
  readonly meKey: string | null;
  /**
   * The 방 이름 field, pre-filled with the export's title. It is the only
   * source of the room name: left as it is, the export title stays; cleared,
   * the report has no title and the cover lists the names instead.
   */
  readonly title: string;
  readonly history: readonly CleanupSnapshot[];
}

export interface CleanupValidation {
  /** Error message per row key. */
  readonly rowErrors: ReadonlyMap<string, string>;
  /** Error for the whole form (for example, fewer than two people). */
  readonly formError: string | null;
  readonly canSubmit: boolean;
}

export const NAME_TOO_LONG_MESSAGE = `이름은 ${MAX_NAME_LENGTH}자까지 쓸 수 있어요.`;
export const DUPLICATE_NAME_MESSAGE = '다른 사람과 이름이 같아요. 같은 사람이면 합치고, 아니면 이름을 바꿔 주세요.';
export const TOO_FEW_PEOPLE_MESSAGE = '성적표를 만들려면 두 사람 이상이 필요해요. 합치기를 되돌려 주세요.';

function rowKey(members: readonly number[]): string {
  return `p${Math.min(...members)}`;
}

/** Display names default to the raw label, trimmed to the 20-character limit. */
export function defaultDisplayName(rawName: string, fallbackIndex: number): string {
  const trimmed = truncateCodePoints(rawName.trim(), MAX_NAME_LENGTH).trim();
  return trimmed || `참여자 ${fallbackIndex + 1}`;
}

export function displayName(row: Pick<CleanupRow, 'name' | 'defaultName'>): string {
  return row.name.trim() || row.defaultName;
}

function sortRows(rows: readonly CleanupRow[]): CleanupRow[] {
  return rows
    .map((row, order) => ({ row, order }))
    .sort((a, b) => b.row.messageCount - a.row.messageCount || a.order - b.order)
    .map(({ row }) => row);
}

export function createCleanupState(summary: Pick<ParseSummary, 'participants' | 'title'>): CleanupState {
  const rows = summary.participants.map((participant, index): CleanupRow => {
    const name = defaultDisplayName(participant.rawName, index);
    return {
      key: rowKey([participant.senderId]),
      name,
      defaultName: name,
      members: [participant.senderId],
      rawNames: [participant.rawName],
      messageCount: participant.messageCount,
      firstAt: participant.firstAt,
      lastAt: participant.lastAt,
      exporterHint: participant.exporterHint,
    };
  });
  const me = rows.find((row) => row.exporterHint);
  return {
    rows: sortRows(rows),
    meKey: me?.key ?? null,
    title: truncateCodePoints((summary.title ?? '').trim(), MAX_TITLE_LENGTH),
    history: [],
  };
}

export function renameRow(state: CleanupState, key: string, name: string): CleanupState {
  if (!state.rows.some((row) => row.key === key)) return state;
  return { ...state, rows: state.rows.map((row) => (row.key === key ? { ...row, name } : row)) };
}

export function setMe(state: CleanupState, key: string | null): CleanupState {
  if (key !== null && !state.rows.some((row) => row.key === key)) return state;
  return { ...state, meKey: key };
}

export function setTitle(state: CleanupState, title: string): CleanupState {
  return { ...state, title };
}

export function isGroupRoom(state: Pick<CleanupState, 'rows'>): boolean {
  return state.rows.length > 2;
}

/**
 * Merges two or more rows into the busiest one (on a tie, the shorter name:
 * "가온" over "가온(PC)"). The step can be undone.
 */
export function mergeRows(state: CleanupState, keys: readonly string[]): CleanupState {
  const selected = new Set(keys);
  const chosen = state.rows.filter((row) => selected.has(row.key));
  if (chosen.length < 2) return state;
  const [main] = chosen
    .map((row, order) => ({ row, order }))
    .sort((a, b) => b.row.messageCount - a.row.messageCount
      || codePointLength(displayName(a.row)) - codePointLength(displayName(b.row))
      || a.order - b.order)
    .map(({ row }) => row) as [CleanupRow, ...CleanupRow[]];
  const members = chosen.flatMap((row) => row.members).sort((a, b) => a - b);
  const merged: CleanupRow = {
    key: rowKey(members),
    name: main.name,
    defaultName: main.defaultName,
    members,
    rawNames: sortRows(chosen).flatMap((row) => row.rawNames),
    messageCount: chosen.reduce((sum, row) => sum + row.messageCount, 0),
    firstAt: Math.min(...chosen.map((row) => row.firstAt)),
    lastAt: Math.max(...chosen.map((row) => row.lastAt)),
    exporterHint: chosen.some((row) => row.exporterHint),
  };
  const rows = sortRows([...state.rows.filter((row) => !selected.has(row.key)), merged]);
  const meKey = state.meKey !== null && selected.has(state.meKey) ? merged.key : state.meKey;
  return {
    ...state,
    rows,
    meKey,
    history: [...state.history, { rows: state.rows, meKey: state.meKey }],
  };
}

export function canUndo(state: CleanupState): boolean {
  return state.history.length > 0;
}

function sameMembers(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Restores the rows from before the last merge. Rows untouched by that merge
 * keep the names typed since then.
 */
export function undoMerge(state: CleanupState): CleanupState {
  const snapshot = state.history[state.history.length - 1];
  if (!snapshot) return state;
  const current = new Map(state.rows.map((row) => [row.key, row]));
  const rows = snapshot.rows.map((row) => {
    const now = current.get(row.key);
    return now && sameMembers(now.members, row.members) ? { ...row, name: now.name } : row;
  });
  const meStillValid = state.meKey !== null && rows.some((row) => {
    const now = current.get(row.key);
    return row.key === state.meKey && now !== undefined && sameMembers(now.members, row.members);
  });
  return {
    ...state,
    rows,
    meKey: meStillValid ? state.meKey : snapshot.meKey,
    history: state.history.slice(0, -1),
  };
}

export function validateCleanup(state: CleanupState): CleanupValidation {
  const rowErrors = new Map<string, string>();
  const seen = new Map<string, string[]>();
  for (const row of state.rows) {
    const name = displayName(row);
    if (codePointLength(name) > MAX_NAME_LENGTH) rowErrors.set(row.key, NAME_TOO_LONG_MESSAGE);
    const sameName = seen.get(name) ?? [];
    sameName.push(row.key);
    seen.set(name, sameName);
  }
  for (const keys of seen.values()) {
    if (keys.length < 2) continue;
    for (const key of keys) if (!rowErrors.has(key)) rowErrors.set(key, DUPLICATE_NAME_MESSAGE);
  }
  const formError = state.rows.length < MIN_PEOPLE ? TOO_FEW_PEOPLE_MESSAGE : null;
  return { rowErrors, formError, canSubmit: formError === null && rowErrors.size === 0 };
}

export function toAnalysisSetup(state: CleanupState): AnalysisSetup {
  const validation = validateCleanup(state);
  if (!validation.canSubmit) throw new Error(validation.formError ?? '이름을 확인해 주세요.');
  const meIndex = state.rows.findIndex((row) => row.key === state.meKey);
  const title = truncateCodePoints(state.title.trim(), MAX_TITLE_LENGTH).trim();
  return {
    people: state.rows.map((row) => ({ name: displayName(row), members: [...row.members] })),
    meIndex: meIndex >= 0 ? meIndex : null,
    title: isGroupRoom(state) && title ? title : null,
  };
}

/** Appends " 2", " 3", ... to repeated names, staying within the length limit. */
export function uniqueNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.map((name) => {
    let candidate = name;
    for (let suffix = 2; used.has(candidate); suffix++) {
      const tail = ` ${suffix}`;
      candidate = `${truncateCodePoints(name, MAX_NAME_LENGTH - tail.length).trim()}${tail}`;
    }
    used.add(candidate);
    return candidate;
  });
}

/**
 * Setup used by the sample (no cleanup screen): everyone as parsed, no "나",
 * and for groups the export title, as if the 방 이름 field were left untouched.
 */
export function defaultAnalysisSetup(summary: Pick<ParseSummary, 'participants' | 'title'>): AnalysisSetup {
  const state = createCleanupState(summary);
  const names = uniqueNames(state.rows.map(displayName));
  const title = truncateCodePoints(state.title, MAX_TITLE_LENGTH).trim();
  return {
    people: state.rows.map((row, index) => ({ name: names[index]!, members: [...row.members] })),
    meIndex: null,
    title: isGroupRoom(state) && title ? title : null,
  };
}
